import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DeleteConfirmDialog from '@/components/dialogs/DeleteConfirmDialog';
import { Crown, User, Shield, Trash2 } from 'lucide-react';
import type { TeamMemberRole, TeamMembership } from '@arrowise/shared';

interface TeamMemberItemProps {
    member: TeamMembership;
    canModify?: boolean;
    onRoleChange?: (memberId: string, newRole: TeamMemberRole, memberName: string) => void;
    onRemove?: (memberId: string, memberName: string) => void;
    showJoinDate?: boolean;
    showRemoveButton?: boolean;
}

export const TeamMemberItem: React.FC<TeamMemberItemProps> = ({
    member,
    canModify = false,
    onRoleChange,
    onRemove,
    showJoinDate = true,
    showRemoveButton = true,
}) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const memberName = member.user?.firstName && member.user?.lastName
        ? `${member.user.firstName} ${member.user.lastName}`
        : member.user?.email || 'Unknown User';

    const getRoleIcon = (role: TeamMemberRole) => {
        switch (role) {
            case 'owner':
                return <Crown className="size-4 text-yellow-600" />;
            case 'admin':
                return <Shield className="size-4 text-blue-600" />;
            default:
                return <User className="size-4 text-gray-600" />;
        }
    };

    const getRoleBadgeVariant = (role: TeamMemberRole) => {
        switch (role) {
            case 'owner':
                return 'default';
            case 'admin':
                return 'secondary';
            default:
                return 'outline';
        }
    };

    return (
        <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
                <div>
                    <p className="font-medium">{memberName}</p>
                    <p className="text-sm text-muted-foreground">
                        {member.user?.email}
                    </p>
                    {showJoinDate && (
                        <p className="text-xs text-muted-foreground">
                            Joined {new Date(member.joinedAt).toLocaleDateString()}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-3">
                {canModify && onRoleChange ? (
                    <Select
                        value={member.role}
                        onValueChange={(newRole: TeamMemberRole) =>
                            onRoleChange(member.userId, newRole, memberName)
                        }
                    >
                        <SelectTrigger className="w-32 cursor-pointer">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="member" className="cursor-pointer">Member</SelectItem>
                            <SelectItem value="admin" className="cursor-pointer">Admin</SelectItem>
                        </SelectContent>
                    </Select>
                ) : (
                    <Badge variant={getRoleBadgeVariant(member.role)} className="gap-1">
                        {getRoleIcon(member.role)}
                        {member.role}
                    </Badge>
                )}
                {canModify && showRemoveButton && onRemove && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmOpen(true)}
                    >
                        <Trash2 />
                        Remove
                    </Button>
                )}
                {canModify && showRemoveButton && onRemove && (
                    <DeleteConfirmDialog
                        open={confirmOpen}
                        onOpenChange={setConfirmOpen}
                        title="Remove member?"
                        description={<>This will remove <strong>{memberName}</strong> from the team. This action cannot be undone.</>}
                        onConfirm={() => onRemove(member.userId, memberName)}
                        confirmLabel="Remove"
                        cancelLabel="Cancel"
                        confirmVariant="destructive"
                    />
                )}
                {!canModify && !showJoinDate && (
                    <p className="text-xs text-muted-foreground">
                        Joined {new Date(member.joinedAt).toLocaleDateString()}
                    </p>
                )}
            </div>
        </div>
    );
};
